import React, { useState } from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiCloud,
  FiLayers,
  FiGitMerge,
  FiCode,
  FiDatabase,
  FiActivity,
  FiShield,
  FiTrendingUp,
  FiRefreshCw,
  FiZap,
  FiCpu,
  FiServer,
  FiCheck,
  FiFileText,
} from "react-icons/fi"
import Link from "../components/Link"
import PageMetadata from "../components/PageMetadata"
import { translateMessageId } from "../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.9rem, 3vw, 2.75rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin: 0 0 0.9rem;
  }
  a:focus-visible,
  button:focus-visible,
  summary:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`

const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  min-width: 0;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`

const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(4, 14, 24, 0.78) 0%,
      rgba(4, 14, 24, 0.55) 43%,
      rgba(4, 14, 24, 0.05) 80%
    );
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
  }
`

const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: clamp(250px, 48vw, 340px);
  }
`

const Kicker = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
`

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #8896a5;
  &:hover {
    border-color: #fff;
  }
`

const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`

const SectionIntro = styled.div`
  max-width: 770px;
  margin-bottom: 2.75rem;
  > p:last-child {
    max-width: 680px;
    font-size: 1.1rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`

const Method = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`

const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 1.5rem 0 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    margin-bottom: 1.75rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    span {
      margin-bottom: 0.75rem;
    }
  }
`

const Related = styled.aside`
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: 1.1rem;
    letter-spacing: 0;
  }
  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.5rem;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    padding: 0.4rem 0;
  }
  a:hover {
    text-decoration: underline;
  }
`

const FAQ = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.4fr;
  gap: 4rem;
  padding-top: 4rem;
  margin-top: 4rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: clamp(1.75rem, 2.5vw, 2.25rem);
  }
  details {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.35rem 0;
    font-size: 1.05rem;
    line-height: 1.4;
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: "+";
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
  details[open] summary::after {
    content: "−";
  }
  summary:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  details p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.8;
    margin: 0;
    padding: 0 2rem 1.5rem 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding-top: 3rem;
    margin-top: 3rem;
  }
`

const FAQSection = styled(Section)`
  ${FAQ} {
    margin: 0;
    padding: 0;
    border-top: 0;
  }
`

const CloudHero = styled(Hero)`
  background: #081522;
  &::after {
    background: linear-gradient(
        90deg,
        rgba(4, 13, 23, 0.88) 0%,
        rgba(4, 13, 23, 0.7) 38%,
        rgba(4, 13, 23, 0.12) 78%
      ),
      linear-gradient(0deg, rgba(4, 13, 23, 0.48), transparent 45%);
  }
`
const HeroContent = styled(Container)`
  min-height: 660px;
  padding-top: 5rem;
  padding-bottom: 5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  h1 {
    color: #fff;
    font-size: clamp(3.25rem, 5.6vw, 5.5rem);
    font-weight: 500;
    line-height: 1.04;
    letter-spacing: -0.055em;
    margin: 0 0 1.75rem;
    max-width: 790px;
  }
  h1 span {
    display: block;
    color: #a7dcf7;
  }
  > p {
    max-width: 510px;
    color: #dae2ec;
    font-size: 1.08rem;
    line-height: 1.8;
    margin: 0 0 2rem;
  }
  @media (max-width: 750px) {
    order: 1;
    min-height: auto;
    padding-block: 3.5rem;
    h1 {
      font-size: clamp(2.65rem, 9vw, 4rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #e7edf5;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin-bottom: 2rem;
  &::before {
    content: "";
    width: 2rem;
    height: 2px;
    background: #91d4f5;
    flex-shrink: 0;
  }
`
const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.4rem;
  background: #91d4f5;
  border: 1px solid #91d4f5;
  color: #111e2c !important;
  padding: 1.05rem 1.35rem;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  transition: background 0.18s ease;
  &:hover {
    background: #bce9ff;
    border-color: #bce9ff;
  }
  svg {
    flex-shrink: 0;
  }
  &:hover svg {
    fill: none;
  }
`
const JourneyNav = styled.nav`
  color: #fff;
  background: #0b1b2a;
  border-top: 1px solid #35404d;
  border-bottom: 1px solid #35404d;
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    padding: 1.7rem 1.75rem;
    color: #fff;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.65rem 1.5rem;
    text-decoration: none;
    border-left: 1px solid #35404d;
  }
  a:last-child {
    border-right: 1px solid #35404d;
  }
  a:hover {
    background: #15293d;
  }
  a:focus-visible {
    outline-color: #91d4f5;
    outline-offset: -5px;
  }
  span {
    display: block;
    color: #a8d9f1;
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    margin-bottom: 0.55rem;
  }
  strong {
    font-size: 1rem;
    font-weight: 500;
  }
  svg {
    align-self: center;
    color: #b9c9dc;
  }
  @media (max-width: 650px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a {
      padding: 1.15rem 0;
      border-left: 0;
      border-bottom: 1px solid #35404d;
    }
    a:last-child {
      border-right: 0;
      border-bottom: 0;
    }
    span {
      display: inline;
      margin: 0 1rem 0 0;
    }
  }
`
const SplitIntro = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.9fr;
  gap: 4rem;
  align-items: end;
  margin-bottom: 3rem;
  h2 {
    max-width: 530px;
    margin-bottom: 0;
    font-size: clamp(2rem, 3.6vw, 3.25rem);
  }
  > p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1.05rem;
    line-height: 1.8;
    margin: 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`
const Services = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`
const Service = styled.article`
  padding: 2rem;
  border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  display: flex;
  flex-direction: column;
  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.75rem;
  }
  > div svg {
    width: 1.65rem;
    height: 1.65rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  > div span {
    font-size: 0.7rem;
    color: ${({ theme }) => theme.colors.text200};
    letter-spacing: 0.1em;
  }
  h3 {
    font-size: 1.2rem;
  }
  > p {
    font-size: 0.95rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0 0 1.5rem;
  }
  > small {
    display: block;
    margin-top: auto;
    font-size: 0.75rem;
    line-height: 1.65;
    color: ${({ theme }) => theme.colors.primary};
  }
  @media (max-width: 400px) {
    padding: 1.5rem;
  }
`
const Architecture = styled(Section)`
  color: #fff;
  background: #081724;
  h2,
  h3 {
    color: #fff;
  }
  ${Kicker} {
    color: #a8d9f1;
  }
  ${SectionIntro} > p:last-child {
    color: #b9c9d9;
  }
  button:focus-visible {
    outline-color: #a8d9f1;
    outline-offset: -5px;
  }
  a:focus-visible {
    outline-color: #a8d9f1;
  }
`
const Selector = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid #354b5f;
  button {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 1.3rem 1.5rem;
    background: transparent;
    border: 0;
    border-right: 1px solid #354b5f;
    border-bottom: 3px solid transparent;
    color: #c4d1df;
    font: inherit;
    cursor: pointer;
    text-align: left;
    min-width: 0;
  }
  button:last-child {
    border-right: 0;
  }
  button:hover {
    background: #142b40;
    color: #fff;
  }
  button[aria-pressed="true"] {
    background: #182e43;
    color: #fff;
    border-bottom-color: #91d4f5;
  }
  svg {
    flex-shrink: 0;
    width: 1.2rem;
    height: 1.2rem;
  }
  span {
    font-size: 0.95rem;
  }
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    button {
      padding: 1rem 1.25rem;
      border-right: 0;
      border-left: 3px solid transparent;
      border-bottom: 1px solid #354b5f;
    }
    button:last-child {
      border-bottom: 0;
    }
    button[aria-pressed="true"] {
      border-left-color: #91d4f5;
      border-bottom-color: #354b5f;
    }
  }
`
const Blueprint = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 3rem;
  padding: 3rem;
  background: #0d2133;
  border: 1px solid #354b5f;
  border-top: 0;
  &[hidden] {
    display: none;
  }
  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  h3 {
    font-size: clamp(1.75rem, 2.5vw, 2.25rem);
    font-weight: 500;
    line-height: 1.2;
  }
  > div > p {
    color: #c1cedc;
    font-size: 1rem;
    line-height: 1.8;
    margin: 0 0 1.75rem;
  }
  h4 {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    color: #a8d9f1;
    margin: 0 0 0.8rem;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem;
  }
  li {
    display: flex;
    gap: 0.7rem;
    margin: 0 0 0.65rem;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #c1cedc;
  }
  li::before {
    display: none;
  }
  li svg {
    flex-shrink: 0;
    color: #a8d9f1;
    margin-top: 0.2rem;
  }
  > div > a {
    margin-top: auto;
    color: #fff;
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 0.9rem;
    text-decoration: none;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #7890a6;
  }
  > div > a:hover {
    border-color: #a8d9f1;
  }
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  @media (max-width: 600px) {
    padding: 1.5rem;
  }
`
const Diagram = styled.figure`
  margin: 0;
  padding: 1.5rem;
  background-color: #0a1a29;
  background-image: linear-gradient(#20364b44 1px, transparent 1px),
    linear-gradient(90deg, #20364b44 1px, transparent 1px);
  background-size: 28px 28px;
  border: 1px solid #354b5f;
  min-width: 0;
  > figcaption {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #b2c5d8;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 1.5rem;
  }
  > ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  > ol > li {
    position: relative;
    display: grid;
    grid-template-columns: 1.5rem 1fr;
    gap: 0.8rem;
    margin: 0;
    padding-bottom: 1rem;
  }
  > ol > li::after {
    content: "";
    position: absolute;
    left: 0.7rem;
    top: 1.7rem;
    bottom: 0;
    border-left: 1px solid #6b8299;
  }
  > ol > li:last-child {
    padding-bottom: 0;
  }
  > ol > li:last-child::after {
    display: none;
  }
  > ol > li > span {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: start;
    margin-top: 0.1rem;
    height: 1.5rem;
    border: 1px solid #748ba3;
    border-radius: 50%;
    color: #a7dcf7;
    background: #0a1a29;
    font-size: 0.65rem;
  }
  > ol > li > div {
    border: 1px solid #3c5771;
    background: #132a40;
    width: 100%;
    padding: 0.8rem 1rem;
  }
  small {
    display: block;
    color: #a8bed3;
    font-size: 0.67rem;
    letter-spacing: 0.02em;
    margin-bottom: 0.3rem;
  }
  strong {
    display: block;
    color: #eef4fa;
    font-size: 0.85rem;
    font-weight: 500;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }
  @media (max-width: 400px) {
    padding: 1rem;
    > ol > li > div {
      padding: 0.75rem;
    }
  }
`
const Foundation = styled.div`
  border-top: 1px solid #3c5771;
  padding-top: 1rem;
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: #b2c5d8;
  font-size: 0.7rem;
  line-height: 1.7;
  > svg {
    color: #a8d9f1;
    flex-shrink: 0;
  }
`
const ArchitectureNote = styled.p`
  color: #aabed0;
  font-size: 0.85rem;
  line-height: 1.7;
  max-width: 870px;
  margin: 1.5rem 0 0;
`
const QualityLayout = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 4.5rem;
  align-items: start;
  > div:first-child p:last-of-type {
    font-size: 1.05rem;
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.text200};
    margin-bottom: 1.75rem;
  }
  > div:first-child a {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`
const Pillars = styled.div`
  display: grid;
  gap: 1.5rem;
  article {
    display: grid;
    grid-template-columns: 1.5rem minmax(0, 1fr);
    column-gap: 1.1rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    padding-top: 1.25rem;
  }
  svg {
    grid-column: 1;
    grid-row: 1 / span 2;
    color: ${({ theme }) => theme.colors.primary};
    width: 1.35rem;
    height: 1.35rem;
    margin-top: 0.1rem;
  }
  h3 {
    grid-column: 2;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  p {
    grid-column: 2;
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.text200};
  }
`
const Deliverables = styled.aside`
  display: grid;
  grid-template-columns: 0.7fr 1.4fr;
  gap: 3rem;
  margin-top: 3rem;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.background};
  h2 {
    font-size: 1.3rem;
    line-height: 1.4;
    margin: 0;
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
  }
  li {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.6;
  }
  li::before {
    display: none;
  }
  li svg {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 0.75rem;
  }
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  @media (max-width: 550px) {
    padding: 1.5rem;
    ul {
      grid-template-columns: 1fr;
    }
  }
`
const Contact = styled(Section)`
  background: #0a1c2e;
  border-top: 3px solid #91d4f5;
  color: #fff;
  ${Kicker} {
    color: #a8d9f1;
  }
  ${Container} {
    display: grid;
    grid-template-columns: 1.25fr 0.75fr;
    gap: 4rem;
    align-items: end;
  }
  h2 {
    color: #fff;
    font-size: clamp(2.5rem, 4.5vw, 4rem);
    max-width: 700px;
    margin-bottom: 0;
  }
  p {
    color: #c1cedc;
    font-size: 1rem;
    line-height: 1.8;
    margin-bottom: 1.75rem;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 900px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`

const services = [
  { id: "foundation", icon: FiLayers },
  { id: "applications", icon: FiCode },
  { id: "hybrid", icon: FiServer },
  { id: "data", icon: FiDatabase },
  { id: "delivery", icon: FiGitMerge },
  { id: "finops", icon: FiTrendingUp },
]
const architectures = [
  {
    id: "apps",
    icon: FiCode,
    layers: [
      "Azure App Service",
      "Azure Functions",
      "Azure SQL Database",
      "Azure Blob Storage",
    ],
  },
  {
    id: "hybrid",
    icon: FiServer,
    layers: [
      "Windows · Linux · Kubernetes",
      "Azure Arc",
      "Azure Policy",
      "Azure Monitor · Microsoft Defender for Cloud",
    ],
  },
  {
    id: "ai",
    icon: FiCpu,
    layers: [
      "Azure Blob Storage",
      "Azure AI Search",
      "Microsoft Foundry · Azure OpenAI",
      "Azure App Service",
    ],
  },
]
const pillars = [
  { id: "operations", icon: FiActivity },
  { id: "security", icon: FiShield },
  { id: "reliability", icon: FiRefreshCw },
  { id: "performance", icon: FiZap },
  { id: "cost", icon: FiTrendingUp },
]
const steps = ["assess", "design", "build", "operate"]
const related = [
  { id: "consulting", to: "/consultoria/" },
  { id: "devsecops", to: "/devsecops/" },
  { id: "ai", to: "/ai-service/" },
  { id: "training", to: "/capacitacion/" },
]

const AzurePage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`azure-service-${id}`, intl)
  const [activeArchitecture, setActiveArchitecture] = useState("apps")
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.hero.childImageSharp.fixed.src}
      />
      <CloudHero>
        <HeroImage
          fluid={data.hero.childImageSharp.fluid}
          alt={t("hero-alt")}
          loading="eager"
          imgStyle={{ objectPosition: "80% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>
            {t("title")}
            <span>{t("title-accent")}</span>
          </h1>
          <p>{t("intro")}</p>
          <Actions>
            <PrimaryLink to="/#contacto">
              {t("contact")}
              <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <SecondaryLink to="#arquitecturas">
              {t("explore")}
              <FiArrowDown aria-hidden="true" />
            </SecondaryLink>
          </Actions>
        </HeroContent>
      </CloudHero>
      <JourneyNav aria-label={t("navigation")}>
        <Container>
          {[
            { id: "services", to: "#servicios" },
            { id: "architecture", to: "#arquitecturas" },
            { id: "method", to: "#ruta" },
          ].map(({ id, to }, i) => (
            <Link key={id} to={to}>
              <div>
                <span aria-hidden="true">0{i + 1}</span>
                <strong>{t(`nav-${id}`)}</strong>
              </div>
              <FiArrowDown aria-hidden="true" />
            </Link>
          ))}
        </Container>
      </JourneyNav>
      <Section id="servicios" aria-labelledby="azure-services-title">
        <Container>
          <SplitIntro>
            <div>
              <Kicker>{t("services-eyebrow")}</Kicker>
              <h2 id="azure-services-title">{t("services-title")}</h2>
            </div>
            <p>{t("services-intro")}</p>
          </SplitIntro>
          <Services>
            {services.map(({ id, icon: Icon }, i) => (
              <Service key={id}>
                <div>
                  <Icon aria-hidden="true" />
                  <span aria-hidden="true">0{i + 1}</span>
                </div>
                <h3>{t(`${id}-title`)}</h3>
                <p>{t(`${id}-description`)}</p>
                <small>{t(`${id}-reference`)}</small>
              </Service>
            ))}
          </Services>
        </Container>
      </Section>
      <Architecture
        id="arquitecturas"
        aria-labelledby="azure-architecture-title"
      >
        <Container>
          <SectionIntro>
            <Kicker>{t("architecture-eyebrow")}</Kicker>
            <h2 id="azure-architecture-title">{t("architecture-title")}</h2>
            <p>{t("architecture-intro")}</p>
          </SectionIntro>
          <Selector role="group" aria-label={t("architecture-selector")}>
            {architectures.map(({ id, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-pressed={activeArchitecture === id}
                aria-controls={`azure-blueprint-${id}`}
                onClick={() => setActiveArchitecture(id)}
              >
                <Icon aria-hidden="true" />
                <span>{t(`architecture-${id}-label`)}</span>
              </button>
            ))}
          </Selector>
          {architectures.map(({ id, layers }) => (
            <Blueprint
              key={id}
              id={`azure-blueprint-${id}`}
              hidden={activeArchitecture !== id}
              role="region"
              aria-labelledby={`azure-blueprint-${id}-title`}
            >
              <div>
                <h3 id={`azure-blueprint-${id}-title`}>
                  {t(`architecture-${id}-title`)}
                </h3>
                <p>{t(`architecture-${id}-description`)}</p>
                <h4>{t("architecture-decisions")}</h4>
                <ul>
                  {[1, 2, 3].map((n) => (
                    <li key={n}>
                      <FiCheck aria-hidden="true" />
                      {t(`architecture-${id}-decision-${n}`)}
                    </li>
                  ))}
                </ul>
                <Link to="/#contacto">
                  {t("architecture-contact")}
                  <FiArrowRight aria-hidden="true" />
                </Link>
              </div>
              <Diagram>
                <figcaption>
                  <FiCloud aria-hidden="true" />
                  {t("architecture-diagram")}
                </figcaption>
                <ol>
                  {layers.map((services, i) => (
                    <li key={services}>
                      <span aria-hidden="true">{i + 1}</span>
                      <div>
                        <small>{t(`architecture-${id}-layer-${i + 1}`)}</small>
                        <strong>{services}</strong>
                      </div>
                    </li>
                  ))}
                </ol>
                <Foundation>
                  <FiShield aria-hidden="true" />
                  <span>{t("architecture-foundation")}</span>
                </Foundation>
              </Diagram>
            </Blueprint>
          ))}
          <ArchitectureNote>{t("architecture-note")}</ArchitectureNote>
        </Container>
      </Architecture>
      <Section aria-labelledby="azure-quality-title">
        <Container>
          <QualityLayout>
            <div>
              <Kicker>{t("quality-eyebrow")}</Kicker>
              <h2 id="azure-quality-title">{t("quality-title")}</h2>
              <p>{t("quality-intro")}</p>
              <Link to="https://learn.microsoft.com/azure/well-architected/">
                {t("quality-source")}
              </Link>
            </div>
            <Pillars>
              {pillars.map(({ id, icon: Icon }) => (
                <article key={id}>
                  <Icon aria-hidden="true" />
                  <h3>{t(`pillar-${id}-title`)}</h3>
                  <p>{t(`pillar-${id}-description`)}</p>
                </article>
              ))}
            </Pillars>
          </QualityLayout>
        </Container>
      </Section>
      <Method id="ruta" aria-labelledby="azure-method-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("method-eyebrow")}</Kicker>
            <h2 id="azure-method-title">{t("method-title")}</h2>
            <p>{t("method-intro")}</p>
          </SectionIntro>
          <Steps>
            {steps.map((id, i) => (
              <li key={id}>
                <span aria-hidden="true">0{i + 1}</span>
                <h3>{t(`method-${id}-title`)}</h3>
                <p>{t(`method-${id}-description`)}</p>
              </li>
            ))}
          </Steps>
          <Deliverables aria-labelledby="azure-deliverables-title">
            <h2 id="azure-deliverables-title">{t("deliverables-title")}</h2>
            <ul>
              {["architecture", "automation", "operations"].map((id) => (
                <li key={id}>
                  <FiFileText aria-hidden="true" />
                  {t(`deliverable-${id}`)}
                </li>
              ))}
            </ul>
          </Deliverables>
          <Related aria-labelledby="azure-related-title">
            <h2 id="azure-related-title">{t("related-title")}</h2>
            <div>
              {related.map(({ id, to }) => (
                <Link key={id} to={to}>
                  {t(`related-${id}`)}
                  <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Related>
        </Container>
      </Method>
      <FAQSection aria-labelledby="azure-faq-title">
        <Container>
          <FAQ>
            <div>
              <Kicker>{t("faq-eyebrow")}</Kicker>
              <h2 id="azure-faq-title">{t("faq-title")}</h2>
            </div>
            <div>
              {["existing", "migration", "cost", "support"].map((id) => (
                <details key={id}>
                  <summary>{t(`faq-${id}-question`)}</summary>
                  <p>{t(`faq-${id}-answer`)}</p>
                </details>
              ))}
            </div>
          </FAQ>
        </Container>
      </FAQSection>
      <Contact aria-labelledby="azure-contact-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="azure-contact-title">{t("cta-title")}</h2>
          </div>
          <div>
            <p>{t("cta-description")}</p>
            <PrimaryLink to="/#contacto">
              {t("contact")}
              <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
          </div>
        </Container>
      </Contact>
    </Page>
  )
}
export default AzurePage
export const query = graphql`
  query AzureServicePageQuery {
    hero: file(relativePath: { eq: "azure/connected-enterprise-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
  }
`
